































































import { Vue, Component, Prop } from "vue-property-decorator";
import {
  RunOverview,
  isActiveRun,
  isTemperatureSet,
  isIdleRun,
} from "../../data/models/RunOverview";
import {
  formatTime,
  formatDurationShort,
  formatDateShort,
} from "../../utils/DateFormatter";

import { hasPlaceholderClockDate } from "@/utils/PanelClockPlaceholders";
import { advanceByMinutes, areOnSameDay } from "@/utils/DateHelper";
import {
  getIresDeviceCategoryFromSerialNumber,
  IresDevice,
  IresDeviceCategory,
} from "@/data/models/IresDevice";
import { formatComBoxRunSensorData } from "@/utils/SensorData";
import { formatKWh } from "@/utils/LocalisationHelper";
import router from "@/router/router";
import { ComBox, getComBoxProgramName } from "@/data/models/ComBox";
import { loadSamplesOfRun } from "@/data/repository/RunRepository";
import { hexToUint8Array } from "@/utils/ByteUtils";
import { IresDeviceResponseReader } from "@/utils/IresDeviceResponseReader";
import { getActiveUser } from "@/data/repository/UserRepository";
import * as base64Buffer from "base64-arraybuffer";
import { markEndDateManualCorrectionsAccess } from "@/permissions/UserPermissions";
import { setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";
import { hasV2OrGreaterSampleData } from "@/data/models/SampleInfo";

@Component
export default class RunItemComponent extends Vue {
  @Prop() serialNumber!: string;
  @Prop() runOverview!: RunOverview;
  @Prop() runAssignmentInfo?: string;
  @Prop() canOpenRunDetails!: boolean;
  @Prop({ default: null }) iresDevice!: IresDevice | null;
  @Prop() elevation!: number;
  @Prop({ default: "" }) backgroundStyle!: string;
  @Prop() doesSupportExtendedSensors!: boolean;
  
  newEndDate = null;
  isAdjustedEndDate = false;
  runDurationInMinutes = -1;

  /** Phone layout (below 600 px). */
  get isPhone() {
    return this.$vuetify.breakpoint.xsOnly;
  }

  /** The readings the phone row lists under the program line, in the desktop columns' order. */
  get phoneRunLines(): string[] {
    const lines = this.showPanelInfo
      ? [this.temperatures, this.stepsAndCycles, this.t0Info, this.heatingPercentageAndPowerConsumption]
      : [this.roomSensorData, this.floorSensorData, this.powerConsumption];

    return lines.filter((line): line is string => !!line);
  }

  get showPanelInfo() {
    return (
      getIresDeviceCategoryFromSerialNumber(this.serialNumber) ===
      IresDeviceCategory.PANEL
    );
  }

  get heatingPercentageAndPowerConsumption(): string {
    if (this.isIdleRun) {
      //Idle run has power consumption or heating info
      return "";
    }

    const powerConsumption = this.hasSampleInfo
      ? parseFloat(this.runOverview.power.toString())
          .toFixed(2)
          .replace(".", ",")
      : "-";

    const percentage = this.hasSampleInfo
      ? Math.floor(this.runOverview.percentage)
      : "-";

    return this.$t("panelDetail.runInfo.heatingPercentageAndPowerConsumption", [
      percentage,
      powerConsumption,
    ]).toString();
  }

  get hasSampleInfo(): boolean {
    return isTemperatureSet(this.runOverview);
  }

  get t0Info(): string {
    if (this.isIdleRun) {
      //Idle run has no t0
      return "";
    }

    const t0 = this.hasSampleInfo ? this.runOverview.t0 : "-";

    return this.$t("panelDetail.runInfo.t0", [t0]).toString();
  }

  get isIdleRun(): boolean {
    return isIdleRun(this.runOverview);
  }

  get stepsAndCycles(): string {
    if (this.isIdleRun) {
      //Idle run has no steps or cycles
      return "";
    }

    const showStepsAndCycles = this.hasSampleInfo;

    const steps = showStepsAndCycles ? this.runOverview.steps : "-";
    const cycles = showStepsAndCycles ? this.runOverview.cycles : "-";

    return this.$t("panelDetail.runInfo.stepsAndCycles", [
      steps,
      cycles,
    ]).toString();
  }

  get floorSensorData() {
    return formatComBoxRunSensorData(this, false, undefined, [
      this.runOverview,
    ]);
  }

  get roomSensorData() {
    return formatComBoxRunSensorData(this, true, undefined, [this.runOverview]);
  }

  get powerConsumption() {
    if (
      this.runOverview.latestMeterReading == undefined ||
      this.runOverview.initialMeterReading == undefined
    ) {
      return null;
    }

    return formatKWh(
      this,
      this.runOverview.latestMeterReading - this.runOverview.initialMeterReading
    );
  }

  get temperatures(): string {
    const showTemps = this.hasSampleInfo;

    const groundMaxTemp = showTemps
      ? this.runOverview.groundMaxTemperature
      : "-";
    const groundMinTemp = showTemps
      ? this.runOverview.groundMinTemperature
      : "-";

    const wallMinTemp = showTemps ? this.runOverview.wallMinTemp : "-";
    const wallMaxTemp = showTemps ? this.runOverview.wallMaxTemp : "-";

    if (groundMaxTemp && groundMinTemp) {
      return this.$t("panelDetail.runInfo.temperatureGroundAndWall", [
        wallMinTemp,
        wallMaxTemp,
        groundMinTemp,
        groundMaxTemp,
      ]).toString();
    }

    return this.$t("panelDetail.runInfo.temperatureWall", [
      wallMinTemp,
      wallMaxTemp,
    ]).toString();
  }

  get programDescription(): string {
    const overview = this.runOverview;
    const chainNumber = overview.chainNumber;

    const numberInChain = overview.numberInChain.toString();

    let programInfo = this.$t("panelDetail.runInfo.programPX", [
      overview.programNumber,
    ]);

    if (this.showPanelInfo) {
      if (overview.programNumber === 0) {
        programInfo = this.$t("panelDetail.runInfo.programMonitor", [
          overview.programNumber,
        ]);
      }
    } else {
      programInfo = getComBoxProgramName(
        this,
        overview.programNumber,
        this.iresDevice ? (this.iresDevice as ComBox) : undefined
      );
    }

    return this.$t("panelDetail.runInfo.formatRunProgramInfo", [
      chainNumber,
      numberInChain,
      programInfo,
    ]).toString();
  }

  onOpenRunDetails() {
    if (!this.canOpenRunDetails) {
      return;
    }

    setLastVisitedItem(
      this.runOverview.id,
      StorageKeyLastVisitedItemLevel.LEVEL_5,
    );

    // ==================== FIRMWARE v4.0.0+ CHANGES ====================
    const doesSupportExtendedSensorsString = this.doesSupportExtendedSensors
      ? "true"
      : "false";
    router.push({
      path: `samples`,
      query: {
        serialNumber: this.serialNumber,
        runId: this.runOverview.id,
        doesSupportExtendedSensors: doesSupportExtendedSensorsString
      },
    });
  }

  get runDuration(): string {
    const runDurationInMinutes = this.runDurationInMinutes !== -1 ? this.runDurationInMinutes : this.runOverview.durationInMin;

    if (isActiveRun(this.runOverview)) {
      const timeDeltaInMs =
        new Date().getTime() - this.runOverview.startDate.toDate().getTime();

      const calculatedDurationInMinutes = Math.min(
        runDurationInMinutes,
        Math.max(Math.floor(timeDeltaInMs / (60 * 1000)), 0)
      );

      return this.$t("panelDetail.runInfo.durationActiveRun", [
        formatDurationShort(this.runOverview.durationInMin),
        formatDurationShort(calculatedDurationInMinutes),
      ]).toString();
    } else {
      return this.$t("panelDetail.runInfo.duration", [
        formatDurationShort(runDurationInMinutes),
      ]).toString();
    }
  }

  created() {
    this.computeNewEndDate();
  }

  getPanelUnit16Value(iresDeviceResponseReader: IresDeviceResponseReader) {
    const uint16 =
      iresDeviceResponseReader.data[iresDeviceResponseReader.currentIndex] +
      iresDeviceResponseReader.data[iresDeviceResponseReader.currentIndex + 1] *
        256;

    iresDeviceResponseReader.skip(2);

    return uint16;
  }

  // To temporarily fix the issue with the battery management for the panels (HDxxx series)
  async computeNewEndDate() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelRunOverview = this.runOverview as any;
    if (hasPlaceholderClockDate(panelRunOverview.endDate)) {
      const isPanel = getIresDeviceCategoryFromSerialNumber(this.serialNumber) === IresDeviceCategory.PANEL;
      const iresDeviceResponseReader = new IresDeviceResponseReader();

      const date = panelRunOverview.startDate.toDate();
      
      let previousTimeDiffInMin = 0;
      let timeDiffToBeAdjusted = 0;
      let didSkipLastSample = false;

      const samples = loadSamplesOfRun(this.serialNumber, panelRunOverview.id);
      samples.then((samplesData) => {
        samplesData.forEach((rawSampleData) => {
          const data = isPanel
            ? hexToUint8Array(rawSampleData.encodedSample)
            : new Uint8Array(
                base64Buffer.decode(rawSampleData.encodedSample)
              );
          iresDeviceResponseReader.setNewData(data);

          if (!isPanel) {
            iresDeviceResponseReader.readUint16();
            iresDeviceResponseReader.readUint8();
            iresDeviceResponseReader.readUint8();

            if (hasV2OrGreaterSampleData(panelRunOverview)) {
              iresDeviceResponseReader.readUint8();
              iresDeviceResponseReader.readUint8();
              iresDeviceResponseReader.readUint8();
            }
          }

          const timeDiffInMinutes_ = isPanel
            ? this.getPanelUnit16Value(
                iresDeviceResponseReader
              )
            : iresDeviceResponseReader.readUint16();
          
          if (rawSampleData.sampleIndex === 0) {
            timeDiffToBeAdjusted = timeDiffInMinutes_;
          }
          
          const timeDiffInMinutes = timeDiffInMinutes_ - timeDiffToBeAdjusted;

          if (previousTimeDiffInMin > timeDiffInMinutes) {
            didSkipLastSample = true;
          }
          else {
            if (didSkipLastSample) {
              didSkipLastSample = false;
            }

            advanceByMinutes(date, timeDiffInMinutes - previousTimeDiffInMin);

            if (rawSampleData.sampleIndex === panelRunOverview.lastLoadedSampleIndex) {
              this.newEndDate = date;
              this.isAdjustedEndDate = true;
              this.runDurationInMinutes = timeDiffInMinutes;
            }

            previousTimeDiffInMin = timeDiffInMinutes;
          }
        });

        if (didSkipLastSample) {
          this.newEndDate = date;
          this.isAdjustedEndDate = true;
          this.runDurationInMinutes = previousTimeDiffInMin;
        }
      });
    }
    else {
      this.newEndDate = panelRunOverview.endDate;
    }
  }

  get activeUser() {
    return getActiveUser();
  }

  get isAllowedToViewMarkedCorrections() {
    return markEndDateManualCorrectionsAccess(this.activeUser);
  }

  get runDescription(): string {
    const overview = this.runOverview;
    const startDate = overview.startDate;

    if (isActiveRun(overview)) {
      return this.$t("panelDetail.runInfo.runDescriptionActive", [
        overview.programRunID,
        formatDateShort(startDate),
        formatTime(startDate),
      ]).toString();
    }

    const endDate = this.newEndDate ?? overview.endDate;

    let translationKey;
    const isSingleDay = areOnSameDay(startDate, endDate, this.isAdjustedEndDate);

    if (this.isAdjustedEndDate && this.isAllowedToViewMarkedCorrections) {
      translationKey = isSingleDay 
        ? "panelDetail.runInfo.runDescriptionSingleDayAdjusted"
        : "panelDetail.runInfo.runDescriptionAdjusted";
    } else {
      translationKey = isSingleDay 
        ? "panelDetail.runInfo.runDescriptionSingleDay"
        : "panelDetail.runInfo.runDescription";
    }

    const formattedDates = isSingleDay 
      ? [overview.programRunID, formatDateShort(endDate), formatTime(endDate)]
      : [overview.programRunID, formatDateShort(startDate), formatDateShort(endDate)];

    return this.$t(translationKey, formattedDates).toString();
  }
}
